import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  GuardResult,
  RedirectCommand,
  Router,
  RouterStateSnapshot,
} from '@angular/router';

import { OidcSecurityService } from 'angular-auth-oidc-client';
import { filter, map, switchMap, take } from 'rxjs';

import { FeatureFlagService } from '@common/service/feature-flag.service';
import { UserService } from '@common/service/user.service';

export const loginGuard: CanActivateFn = (route, state) => {
  const userService = inject(UserService);
  const featureFlagService = inject(FeatureFlagService);
  const oidcSecurityService = inject(OidcSecurityService);
  const router = inject(Router);

  const loginParam = route.queryParamMap.get('login');

  // No directive to act on — the overwhelmingly common case, so no auth check, no redirect.
  if (loginParam === null) {
    return true;
  }

  // AK-731: the flag is a one-shot directive, not state. Anything other than `true` has nothing
  // for us to do, so consume it immediately rather than letting it ride along in the URL.
  if (loginParam !== 'true') {
    return consumeLoginParam(state, router);
  }

  const decide = (isAuthenticated: boolean): GuardResult => {
    if (!isAuthenticated) {
      userService.login(returnTarget(route, state, router));
      return false;
    }
    // Authenticated: the directive is satisfied, so drop it from the URL (AK-731). Left in place it
    // gets bookmarked/shared, re-arms this guard on every later activation of the route, and rides
    // along on the app's `queryParamsHandling: 'merge'` navigations.
    return consumeLoginParam(state, router);
  };

  // AK-476 (WP-F5): in cookie-session mode there is no OIDC config to await —
  // read auth state directly from the API-owned session.
  if (featureFlagService.cookieSessionMigrationEnabled) {
    return userService.isUserAuthenticated$.pipe(take(1), map<boolean, GuardResult>(decide));
  }

  // Wait for OIDC configuration to be loaded before checking authentication
  return oidcSecurityService.getConfiguration().pipe(
    filter((config) => !!config?.configId),
    take(1),
    switchMap(() => userService.isUserAuthenticated$),
    map<boolean, GuardResult>(decide),
  );
};

/**
 * AK-731 — redirect to the URL the user asked for, minus the now-consumed `login` flag. Mirrors
 * `entitySwitchGuard`'s `?entity=` strip: re-serialise `state.url` (query params AND fragment
 * intact) without the one param. `replaceUrl` is explicit so the consumed directive leaves no
 * history entry for Back to land on.
 */
function consumeLoginParam(state: RouterStateSnapshot, router: Router): RedirectCommand {
  const tree = router.parseUrl(state.url);
  delete tree.queryParams['login'];
  return new RedirectCommand(tree, { replaceUrl: true });
}

/**
 * The page the user was trying to reach, to return to after login. Uses the full
 * attempted URL (`state.url`, e.g. `/modules?login=true`) — NOT the guard's
 * `route.url` segments, which can be empty for lazy/child routes and previously
 * collapsed the target to `/`. The `login` flag is stripped so the return trip
 * doesn't re-trigger the guard.
 */
function returnTarget(
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  router: Router,
): { pathname: string; hash: string } {
  // Re-serialise the router tree (the app's DialogAwareUrlSerializer keeps `dlg` verbatim) instead of
  // round-tripping the query through URLSearchParams, which would decode and re-encode dialog values.
  const tree = router.parseUrl(state.url);
  delete tree.queryParams['login'];
  const serialized = router.serializeUrl(tree);
  const hashAt = serialized.indexOf('#');
  return {
    pathname: hashAt < 0 ? serialized : serialized.slice(0, hashAt),
    hash: route.fragment ? `#${route.fragment}` : '',
  };
}
